import type { FC } from 'react';
import React, { useEffect, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';

import type { EventDesignThemingProviderProps, FontPreloadLinkAttributes } from './types.internal';
import { useEventFontsLoader } from './useEventFontsLoader';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { EventDesignThemingContextProvider as Provider, useEventDesignThemingProvider } from './EventDesignThemingContext';

/**
 * Stabilizes font links for react-helmet-async during HMR.
 *
 * Problem: react-helmet-async's HelmetProvider context persists across HMR, causing stale state.
 * When components HMR, React state resets but HelmetProvider's internal context doesn't.
 * This causes Helmet to ignore "new" links thinking they're already rendered.
 *
 * Solution: Force Helmet to unmount/remount when links change, clearing its internal state.
 * This is HMR-specific and only runs in development mode.
 */
const useStabilizedFontLinksDuringHMR = (fontLoadLinks: FontPreloadLinkAttributes) => {
  const [renderHelmet, setRenderHelmet] = useState(true);

  // Force Helmet to unmount and remount when links change (fixes HMR issue in dev mode)
  useEffect(() => {
    if (__DEV__ && fontLoadLinks.length > 0) {
      // Briefly unmount Helmet to force react-helmet-async to clear its internal state
      setRenderHelmet(false);
      // Use Promise to ensure unmount happens before remount
      Promise.resolve().then(() => setRenderHelmet(true));
    }
  }, [fontLoadLinks]);

  return { renderHelmet, fontLoadLinks };
};

/**
 * EventDesignThemingProvider - Manages font loading and theme computation with dual-mode strategy:
 *
 * 1. Initial Page Load:
 *    - Preloads fonts via <Helmet> tags in document <head>
 *    - Marks fonts as "loaded" after 100ms (allows fast theme application)
 *    - Updates theme 3 times: before fonts load, after fonts load, if fonts fail
 *    - Uses font-display: swap to prevent render blocking
 *    - Result: Fast LCP, no FOUT, progressive rendering
 *
 * 2. Dynamic Font Changes (Website Designer preview):
 *    - Uses WebFont.load() to detect when new fonts are ready
 *    - Waits for fontactive callback before marking as "loaded"
 *    - Updates theme 3 times: before fonts load, after fonts load, if fonts fail
 *    - Prevents theme from applying before fonts are available
 *    - Result: No flickering/FOUT during font pack changes
 *
 * This dual approach optimizes initial page load while preserving smooth
 * font transitions in the website designer preview experience.
 */
const EventDesignThemingProvider: FC<EventDesignThemingProviderProps> = ({ eventDesign, children }) => {
  // Self-hosted Google Fonts: when enabled, fonts are loaded from Joy CDN instead of Google.
  // The Amplitude flag payload contains { migrated: string[], omitted: string[] }:
  //   - migrated: font families that have been mirrored to S3 (use self-hosted path)
  //   - omitted: commercial/non-Google fonts explicitly excluded (fall back to Google silently)
  // Families not in either list also fall back to Google. See: scripts/font-mirror/README.md
  const [isSelfHostedFontsEnabled, , , selfHostedFontsPayload] = useIsFeatureEnabled('websiteSelfHostedGoogleFontsEnabled');
  const payloadObj =
    selfHostedFontsPayload && typeof selfHostedFontsPayload === 'object' && !Array.isArray(selfHostedFontsPayload)
      ? (selfHostedFontsPayload as { migrated?: string[]; omitted?: string[] })
      : undefined;
  const mirroredFamilies = payloadObj?.migrated;
  // Per-line rich-title fonts (eventName.contentHtml) are only worth downloading when the
  // multiline-title render is enabled — flag-off guests fall back to the plain display name.
  const [isMultilineTitleEnabled] = useIsFeatureEnabled('websiteHeaderMultilineTitleEnabled');
  const { fontLoadLinks, loadedFonts, addFonts, hasFont, theme, isThemeCurrent, loadingFonts } = useEventFontsLoader(eventDesign, {
    useSelfHostedFonts: isSelfHostedFontsEnabled,
    mirroredFamilies,
    shouldLoadHeaderTitleFonts: isMultilineTitleEnabled,
  });
  const { renderHelmet, fontLoadLinks: stabilizedFontLinks } = useStabilizedFontLinksDuringHMR(fontLoadLinks);
  const [shouldResetLetterSpacing] = useIsFeatureEnabled('guestResetLetterSpacingForFontPack');

  const value = useMemo(
    () => ({
      eventDesign,
      addFonts,
      hasFont,
      loadedFonts,
      theme,
      isThemeCurrent,
      loadingFonts,
      shouldResetLetterSpacing,
    }),
    [eventDesign, addFonts, hasFont, loadedFonts, theme, isThemeCurrent, loadingFonts, shouldResetLetterSpacing],
  );

  return (
    <Provider value={value}>
      {/* Preload fonts via Helmet for initial page load */}
      {renderHelmet && (
        <Helmet>
          {stabilizedFontLinks.map(({ key, ...linkProps }) => (
            <link
              key={key}
              {...linkProps}
            />
          ))}
        </Helmet>
      )}
      {children}
    </Provider>
  );
};

export { EventDesignThemingProvider, useEventDesignThemingProvider };
